import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';

import { FilterZustand, LEERE_FILTER, Mitarbeiter, Projekt } from '../../types/zeiterfassung';

type Props = {
  filter: FilterZustand;
  onFilterAendern: (aenderung: Partial<FilterZustand>) => void;
  onZuruecksetzen: () => void;
  projekte: Projekt[];
  mitarbeiter: Mitarbeiter[];
  darfMitarbeiterFiltern: boolean;
  anzahl: number;
  onExport: () => void;
  exportLaeuft: boolean;
};

const ZeiteintragFilter = ({
  filter,
  onFilterAendern,
  onZuruecksetzen,
  projekte,
  mitarbeiter,
  darfMitarbeiterFiltern,
  anzahl,
  onExport,
  exportLaeuft
}: Props) => {
  const [offen, setOffen] = useState(false);
  const [sucheRoh, setSucheRoh] = useState(filter.suche);

  // Die Suche läuft serverseitig; ohne diese Verzögerung würde jeder
  // Tastendruck eine eigene Anfrage auslösen.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (sucheRoh !== filter.suche) {
        onFilterAendern({ suche: sucheRoh });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [sucheRoh, filter.suche, onFilterAendern]);

  useEffect(() => {
    // Beim Zurücksetzen von außen auch das Suchfeld leeren.
    if (filter.suche === '' && sucheRoh !== '') {
      setSucheRoh('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.suche]);

  const aktiveFilter = [
    filter.projectId && 'Projekt',
    filter.userId && 'Mitarbeiter',
    filter.dateFrom && 'Von',
    filter.dateTo && 'Bis',
    filter.suche && 'Suche'
  ].filter(Boolean) as string[];

  const alleZuruecksetzen = () => {
    setSucheRoh('');
    onZuruecksetzen();
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 1
        }}
      >
        <TextField
          size="small"
          placeholder="Suchen (Beschreibung, Projekt, Mitarbeiter)"
          value={sucheRoh}
          onChange={e => setSucheRoh(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          inputProps={{ 'aria-label': 'Zeiteinträge durchsuchen' }}
          sx={{ flexGrow: 1, minWidth: 240 }}
        />

        <Button startIcon={<FilterListIcon />} onClick={() => setOffen(!offen)}>
          Filter {aktiveFilter.length > 0 && `(${aktiveFilter.length})`}
        </Button>

        <Button
          startIcon={<DownloadIcon />}
          onClick={onExport}
          disabled={exportLaeuft || anzahl === 0}
        >
          CSV-Export
        </Button>

        {aktiveFilter.length > 0 && (
          <Button size="small" onClick={alleZuruecksetzen}>
            Zurücksetzen
          </Button>
        )}
      </Box>

      {aktiveFilter.length > 0 && !offen && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {aktiveFilter.map(name => (
            <Chip key={name} label={name} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      <Collapse in={offen}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-projekt-label">Projekt</InputLabel>
                  <Select
                    labelId="filter-projekt-label"
                    value={filter.projectId}
                    onChange={(e: SelectChangeEvent) =>
                      onFilterAendern({ projectId: e.target.value })
                    }
                    label="Projekt"
                  >
                    <MenuItem value="">Alle Projekte</MenuItem>
                    {projekte.map(projekt => (
                      <MenuItem key={projekt.id} value={String(projekt.id)}>
                        {projekt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {darfMitarbeiterFiltern && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="filter-mitarbeiter-label">Mitarbeiter</InputLabel>
                    <Select
                      labelId="filter-mitarbeiter-label"
                      value={filter.userId}
                      onChange={(e: SelectChangeEvent) =>
                        onFilterAendern({ userId: e.target.value })
                      }
                      label="Mitarbeiter"
                    >
                      <MenuItem value="">Alle Mitarbeiter</MenuItem>
                      {mitarbeiter.map(m => (
                        <MenuItem key={m.id} value={String(m.id)}>
                          {m.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Von"
                  value={filter.dateFrom}
                  onChange={wert => onFilterAendern({ dateFrom: wert })}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Bis"
                  value={filter.dateTo}
                  minDate={filter.dateFrom ?? undefined}
                  onChange={wert => onFilterAendern({ dateTo: wert })}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button size="small" onClick={alleZuruecksetzen}>
                Alle Filter zurücksetzen
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Collapse>
    </Box>
  );
};

export { LEERE_FILTER };
export default ZeiteintragFilter;
